import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import Select from "react-select";
import { 
  X, Briefcase, Save, AlertCircle, User, Mail, Phone, 
  GraduationCap, Calendar, MapPin, Award, Info 
} from "lucide-react";

const API_URL = "https://ims.holdthechild.net/api";


const Section = ({ title, icon, children }) => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <div className="border border-gray-200 rounded-xl mb-4 overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left px-5 py-3 bg-gray-50 font-semibold text-gray-700 flex justify-between items-center hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <span className="text-orange-500">{icon}</span>
          <span>{title}</span>
        </div>
        <span className="text-gray-500 text-lg">{isOpen ? "−" : "+"}</span>
      </button>
      {isOpen && <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-5">{children}</div>}
    </div>
  );
};

export default function EditApplicantModal({ applicant, onClose, onUpdated }) {
  // Initialize with empty state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    jobId: "",
    skills: "",
    experience: "",
    education: "",
    source: "",
    status: "Applied",
    appliedOn: new Date().toISOString().split("T")[0],
  });

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const modalRef = useRef(null);
  const token = localStorage.getItem("token");
  const experienceOptions = [
  { value: "Entry Level (0–1 years)", label: "Entry Level (0–1 years)" },
  { value: "Junior (1–3 years)", label: "Junior (1–3 years)" },
  { value: "Mid-Level (3–5 years)", label: "Mid-Level (3–5 years)" },
  { value: "Senior (5–8 years)", label: "Senior (5–8 years)" },
  { value: "Lead / Manager (8+ years)", label: "Lead / Manager (8+ years)" },
];

const educationOptions = [
  { value: "High School", label: "High School" },
  { value: "Diploma", label: "Diploma" },
  { value: "Bachelor’s Degree", label: "Bachelor’s Degree" },
  { value: "Postgraduate Diploma", label: "Postgraduate Diploma" },
  { value: "Master’s Degree", label: "Master’s Degree" },
  { value: "PhD", label: "PhD" },
  { value: "Professional Certification", label: "Professional Certification" },
];

  const sourceOptions = [
    { value: "Email", label: "Email" },
    { value: "Referral", label: "Referral" },
    { value: "Walk-in", label: "Walk-in" },
    { value: "LinkedIn", label: "LinkedIn" },
    { value: "Job Portal", label: "Job Portal" },
  ];

  const statusOptions = [
    { value: "Applied", label: "Applied" },
    { value: "Pending", label: "Pending" },
    { value: "Shortlisted", label: "Shortlisted" },
    { value: "Successful", label: "Successful" },
    { value: "Unsuccessful", label: "Unsuccessful" },
  ];

  // Fetch jobs safely
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await axios.get(`${API_URL}/job-posts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setJobs(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (err) {
        console.error("Error fetching jobs:", err);
        setJobs([]);
      }
    };
    fetchJobs();
  }, [token]);

  // Pre-fill form when editing
  useEffect(() => {
    console.log("applicant changed:", applicant); // Debug log
    
    if (applicant && applicant._id) {
      const newFormData = {
        fullName: applicant.fullName || "",
        email: applicant.email || "",
        phone: applicant.phone || "",
        jobId: applicant.jobId?._id || applicant.jobId || "",
        skills: applicant.skills || "",
        experience: applicant.experience || "",
        education: applicant.education || "",
        source: applicant.source || "",
        status: applicant.status || "Applied",
        appliedOn: applicant.appliedOn
          ? new Date(applicant.appliedOn).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
      };
      
      console.log("Setting form data to:", newFormData); // Debug log
      setFormData(newFormData);
    }
  }, [applicant]); // Only depend on applicant

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const endpoint = `${API_URL}/applicants/${applicant._id}`;
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      await axios.put(endpoint, formData, config);
      setSuccess("Applicant updated successfully!");
      toast.success("Applicant updated successfully");
      onUpdated?.();
      setTimeout(() => {
        onClose();
        setSuccess("");
      }, 1500);
    } catch (err) {
      console.error(err);
      const message =
        err.response?.data?.message ||
        err.response?.statusText ||
        "Failed to update applicant";
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Clear notifications after 3 seconds
  useEffect(() => {
    let timer;
    if (error || success) {
      timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [error, success]);

  const jobOptions = Array.isArray(jobs)
    ? jobs.map((job) => ({ value: job._id, label: job.vacancy }))
    : [];

  const renderField = (label, name, type = "text", icon, required = false) => (
    <div className="space-y-1">
      <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {icon}
          </div>
        )}
        <input
          name={name}
          type={type}
          value={formData[name] || ""}
          onChange={handleChange}
          required={required}
          className={`w-full border rounded-lg px-3 py-2.5 ${icon ? 'pl-10' : 'pl-3'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors`}
        />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      {/* Popup Notifications */}
      {error && (
        <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow-lg flex items-center space-x-3 animate-fade-in-out">
            <AlertCircle className="text-red-500 flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}
      {success && (
        <div className="fixed top-5 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-xl shadow-lg flex items-center space-x-3 animate-fade-in-out">
            <AlertCircle className="text-green-500 flex-shrink-0" />
            <span>{success}</span>
          </div>
        </div>
      )}

      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out"
      >
        {/* Header with Orange Gradient */}
        <div className="flex-shrink-0 bg-gradient-to-r from-orange-200 to-orange-200 border-b border-orange-50">
          <div className="flex items-center justify-between px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                <Briefcase className="w-7 h-7 text-black" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-black">Edit Applicant</h2>
                <p className="text-black-100 mt-1">Recruitment Management</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Body - FIXED: Added overflow-visible and relative positioning */}
        <div className="flex-grow px-8 py-6 overflow-y-auto relative">
          <form id="edit-applicant-form" onSubmit={handleSubmit} className="space-y-6">
            <Section title="Personal Information" icon={<User />}>
              {renderField("Full Name", "fullName", "text", <User className="h-4 w-4 text-gray-400" />, true)}
              {renderField("Email", "email", "email", <Mail className="h-4 w-4 text-gray-400" />, true)}
              {renderField("Phone", "phone", "text", <Phone className="h-4 w-4 text-gray-400" />, true)}
            </Section>

            <Section title="Application Details" icon={<Briefcase />}>
              <div className="space-y-1">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  Job Applied <span className="text-red-500">*</span>
                </label>
                <div className="z-50">
                  <Select
                    options={jobOptions}
                    value={jobOptions.find((opt) => opt.value === formData.jobId) || null}
                    onChange={(selected) =>
                      setFormData((prev) => ({ ...prev, jobId: selected?.value || "" }))
                    }
                    placeholder="Select Job..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderRadius: '0.5rem',
                        minHeight: '42px',
                        borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                        boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                        '&:hover': {
                          borderColor: '#F97316'
                        }
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: '#9CA3AF'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: '#1F2937'
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                        color: state.isSelected ? 'white' : '#1F2937',
                        '&:hover': {
                          backgroundColor: '#FFEDD5',
                          color: '#1F2937'
                        }
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: '#6B7280',
                        '&:hover': {
                          color: '#F97316'
                        }
                      }),
                      indicatorSeparator: (base) => ({
                        ...base,
                        backgroundColor: '#D1D5DB'
                      }),
                      menu: (base) => ({
                        ...base,
                        borderRadius: '0.5rem',
                        border: '1px solid #E5E7EB',
                        zIndex: 9999
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  Experience <span className="text-red-500">*</span>
                </label>
                <div className="z-50">
                  <Select
                    options={experienceOptions}
                    value={experienceOptions.find((opt) => opt.value === formData.experience) || null}
                    onChange={(selected) =>
                      setFormData((prev) => ({ ...prev, experience: selected?.value || "" }))
                    }
                    placeholder="Select Experience..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderRadius: '0.5rem',
                        minHeight: '42px',
                        borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                        boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                        '&:hover': {
                          borderColor: '#F97316'
                        }
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: '#9CA3AF'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: '#1F2937'
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                        color: state.isSelected ? 'white' : '#1F2937',
                        '&:hover': {
                          backgroundColor: '#FFEDD5',
                          color: '#1F2937'
                        }
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: '#6B7280',
                        '&:hover': {
                          color: '#F97316'
                        }
                      }),
                      indicatorSeparator: (base) => ({
                        ...base,
                        backgroundColor: '#D1D5DB'
                      }),
                      menu: (base) => ({
                        ...base,
                        borderRadius: '0.5rem',
                        border: '1px solid #E5E7EB',
                        zIndex: 9999
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  Education <span className="text-red-500">*</span>
                </label>
                <div className="z-50">
                  <Select
                    options={educationOptions}
                    value={educationOptions.find((opt) => opt.value === formData.education) || null}
                    onChange={(selected) =>
                      setFormData((prev) => ({ ...prev, education: selected?.value || "" }))
                    }
                    placeholder="Select Education..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderRadius: '0.5rem',
                        minHeight: '42px',
                        borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                        boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                        '&:hover': {
                          borderColor: '#F97316'
                        }
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: '#9CA3AF'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: '#1F2937'
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                        color: state.isSelected ? 'white' : '#1F2937',
                        '&:hover': {
                          backgroundColor: '#FFEDD5',
                          color: '#1F2937'
                        }
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: '#6B7280',
                        '&:hover': {
                          color: '#F97316'
                        }
                      }),
                      indicatorSeparator: (base) => ({
                        ...base,
                        backgroundColor: '#D1D5DB'
                      }),
                      menu: (base) => ({
                        ...base,
                        borderRadius: '0.5rem',
                        border: '1px solid #E5E7EB',
                        zIndex: 9999
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                </div>
              </div>

              {renderField("Skills", "skills", "text", <Award className="h-4 w-4 text-gray-400" />, true)}
             
            </Section>
            

            <Section title="Application Status" icon={<Info />}>
              <div className="space-y-1">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  Source <span className="text-red-500">*</span>
                </label>
                <div className="z-50">
                  <Select
                    options={sourceOptions}
                    value={sourceOptions.find((opt) => opt.value === formData.source) || null}
                    onChange={(selected) =>
                      setFormData((prev) => ({ ...prev, source: selected?.value || "" }))
                    }
                    placeholder="Select Source..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderRadius: '0.5rem',
                        minHeight: '42px',
                        borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                        boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                        '&:hover': {
                          borderColor: '#F97316'
                        }
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: '#9CA3AF'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: '#1F2937'
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                        color: state.isSelected ? 'white' : '#1F2937',
                        '&:hover': {
                          backgroundColor: '#FFEDD5',
                          color: '#1F2937'
                        }
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: '#6B7280',
                        '&:hover': {
                          color: '#F97316'
                        }
                      }),
                      indicatorSeparator: (base) => ({
                        ...base,
                        backgroundColor: '#D1D5DB'
                      }),
                      menu: (base) => ({
                        ...base,
                        borderRadius: '0.5rem',
                        border: '1px solid #E5E7EB',
                        zIndex: 9999
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  Status <span className="text-red-500">*</span>
                </label>
                <div className="z-50">
                  <Select
                    options={statusOptions}
                    value={statusOptions.find((opt) => opt.value === formData.status) || null}
                    onChange={(selected) =>
                      setFormData((prev) => ({ ...prev, status: selected?.value || "" }))
                    }
                    placeholder="Select Status..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderRadius: '0.5rem',
                        minHeight: '42px',
                        borderColor: state.isFocused ? '#F97316' : '#D1D5DB',
                        boxShadow: state.isFocused ? '0 0 0 1px #F97316' : 'none',
                        '&:hover': {
                          borderColor: '#F97316'
                        }
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: '#9CA3AF'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: '#1F2937'
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? '#F97316' : state.isFocused ? '#FFEDD5' : 'white',
                        color: state.isSelected ? 'white' : '#1F2937',
                        '&:hover': {
                          backgroundColor: '#FFEDD5',
                          color: '#1F2937'
                        }
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: '#6B7280',
                        '&:hover': {
                          color: '#F97316'
                        }
                      }),
                      indicatorSeparator: (base) => ({
                        ...base,
                        backgroundColor: '#D1D5DB'
                      }),
                      menu: (base) => ({
                        ...base,
                        borderRadius: '0.5rem',
                        border: '1px solid #E5E7EB',
                        zIndex: 9999
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                </div>
              </div>

              {renderField("Applied On", "appliedOn", "date", <Calendar className="h-4 w-4 text-gray-400" />)}
            </Section>
          </form>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Info className="w-4 h-4 text-orange-500" />
                <span>Make sure to save your changes</span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200"
                disabled={submitting}
              >
                Cancel
              </button>

              <button
                type="submit"
                form="edit-applicant-form"
                className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${
                  submitting
                    ? 'bg-orange-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 hover:scale-105 shadow-lg hover:shadow-xl'
                }`}
                disabled={submitting}
              >
                {submitting ? (
                  <div className="flex items-center space-x-3">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Saving Changes...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Save className="w-5 h-5" />
                    <span>Update Applicant</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}